import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { toast } from "sonner";
import {
  quotesFeed, getQuoteInvoiceable, createInvoiceFromQuote, getCompany,
} from "../../lib/api";
import { Loader2, AlertCircle, FileText, ArrowLeft } from "lucide-react";
import { TAX_REGIME_KEYS, TAX_REGIME_LABELS, TAX_REGIME_HINTS } from "../../lib/taxRegimes";

// v6 F-Invoicing F2 — Wizard "Factuur vanuit goedgekeurde offerte".
// UX-principe: MAXIMAAL VOORAF INVULLEN op basis van offerte + project.
// Gebruiker kiest enkel: type factuur + percentage/vast bedrag + optionele
// omschrijving-tweak. Alle rest (klant, project, BTW, quote-referentie,
// snapshots) komt automatisch mee.

const TYPE_LABELS = {
  deposit: "Voorschot",
  interim: "Tussenfactuur",
  final: "Eindfactuur",
};

// Standaard-omschrijving per type — korte referentietekst, GEEN technische
// offerte-inhoud. Formaat: "{Type}factuur conform offerte #{nr} d.d. {datum}".
// Gebruiker mag deze omschrijving altijd manueel aanpassen (descTouched).
const _fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("nl-BE"); }
  catch { return ""; }
};
const _defaultDescription = (type, quoteNumber, quoteDate) => {
  const qn = quoteNumber || "";
  const dt = _fmtDate(quoteDate);
  const suffix = qn ? ` #${qn}${dt ? ` d.d. ${dt}` : ""}` : "";
  if (type === "deposit") return `Voorschotfactuur conform offerte${suffix}`;
  if (type === "interim") return `Tussenfactuur conform offerte${suffix}`;
  return `Eindfactuur conform offerte${suffix}`;
};

const fmtEur = (n) =>
  new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR" })
    .format(Number(n || 0));

export default function FactuurVanuitOfferteDialog({ open, onOpenChange, presetQuoteId = null }) {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [quoteId, setQuoteId] = useState(presetQuoteId || "");
  const [info, setInfo] = useState(null);        // GET /invoiceable respons
  const [infoLoading, setInfoLoading] = useState(false);
  const [type, setType] = useState("deposit");
  const [mode, setMode] = useState("percent");   // "percent" | "amount"
  const [percent, setPercent] = useState(30);
  const [amount, setAmount] = useState(0);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // v6 F3.5b — Regime state + company-default percentages.
  const [regime, setRegime] = useState("standard");
  const [companyPcts, setCompanyPcts] = useState({ deposit: 30, interim: 40, final: 30 });

  // Load company-defaults for percentages (once, non-blocking).
  useEffect(() => {
    if (!open) return;
    getCompany().then((c) => {
      setCompanyPcts({
        deposit: Number(c?.default_deposit_pct ?? 30),
        interim: Number(c?.default_interim_pct ?? 40),
        final: Number(c?.default_final_pct ?? 30),
      });
    }).catch(() => {});
  }, [open]);

  // Load approved quotes list zodra dialoog opent.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const rows = await quotesFeed({ status: "approved" });
        setQuotes(rows || []);
      } catch {
        toast.error("Kon offertes niet laden.");
      }
    })();
  }, [open]);

  // Als er een preset quoteId is → gebruik die.
  useEffect(() => {
    if (open && presetQuoteId) setQuoteId(presetQuoteId);
  }, [open, presetQuoteId]);

  // Bij offerte-keuze → invoiceable info ophalen (remaining, snapshots, blocker).
  useEffect(() => {
    if (!quoteId) { setInfo(null); return; }
    setInfoLoading(true);
    getQuoteInvoiceable(quoteId)
      .then((data) => {
        setInfo(data);
        // v14r — Regime volgt in eerste instantie de offerte-BTW:
        //   6% → reduced_renovation, anders klant-default of standard.
        const quoteVat = Number(data?.default_vat_rate ?? 21);
        if (quoteVat === 6) {
          setRegime("reduced_renovation");
        } else if (data?.client_default_tax_regime_key) {
          setRegime(data.client_default_tax_regime_key);
        } else {
          setRegime("standard");
        }
      })
      .catch(() => { setInfo(null); toast.error("Kon offertegegevens niet ophalen."); })
      .finally(() => setInfoLoading(false));
  }, [quoteId]);

  // v6 F3.5b — Bij type-wissel → vul percent-default vanuit CompanySettings.
  // Eindfactuur wordt hieronder alsnog altijd geclamped op remaining.
  useEffect(() => {
    if (type === "deposit") setPercent(companyPcts.deposit);
    else if (type === "interim") setPercent(companyPcts.interim);
    else setPercent(companyPcts.final);
  }, [type, companyPcts]);

  // Auto-set default type op "eind" wanneer remaining <= totaal (heuristiek):
  // laat gebruiker gewoon zelf kiezen — geen surprise defaults.
  // Percentage → bedrag update.
  const remaining = info?.remaining_excl ?? 0;
  // v6 F3.5b — HARDE BUSINESSREGEL: eindfactuur factureert ALTIJD exact
  // het resterend factureerbaar saldo, ongeacht ingesteld percentage.
  const isFinal = type === "final";
  const computedAmountRaw = mode === "percent"
    ? Math.round((remaining * Number(percent || 0) / 100) * 100) / 100
    : Number(amount || 0);
  const computedAmount = isFinal ? Math.round(remaining * 100) / 100 : computedAmountRaw;
  const vatRate = info?.default_vat_rate ?? 21;
  // v14q — vat_buckets forward-exposed. Momenteel altijd één bucket
  // (calculate_lines gebruikt project_vat als SSOT), maar we lezen ze al zodat
  // een toekomstige mixed-VAT contract geen breuk veroorzaakt.
  const vatBuckets = info?.vat_buckets || {};
  const activeBuckets = Object.entries(vatBuckets)
    .filter(([, v]) => Number(v) > 0)
    .map(([k]) => Number(k))
    .sort((a, b) => a - b);
  const vatBucketsLabel =
    activeBuckets.length > 1 ? activeBuckets.map(r => `${r}%`).join(" + ") : `${vatRate}%`;
  const isReverseCharge = regime === "reverse_charge";
  // v14q — Coherentie-check: reduced_renovation regime (6%) ↔ offerte-tarief 21%.
  // Enige echte tegenstrijdigheid — 'standard' regime is agnostisch.
  const showRegimeMismatchWarning =
    regime === "reduced_renovation" && vatRate === 21 && !isReverseCharge;
  // v14r — Effectief factuurtarief = regime-mapping (matcht backend):
  // standard→21, reduced_renovation→6, reverse_charge→0.
  const effectiveVatRate =
    regime === "reverse_charge" ? 0
    : regime === "reduced_renovation" ? 6
    : 21;
  // v6 F3.5b — reverse_charge: totalen op factuur worden bij finalize op 0
  // gezet. Preview toont deze forced-zero al om verrassing te vermijden.
  const previewVat = isReverseCharge
    ? 0
    : Math.round(computedAmount * effectiveVatRate) / 100;
  const previewIncl = isReverseCharge
    ? Math.round(computedAmount * 100) / 100
    : Math.round((computedAmount + previewVat) * 100) / 100;

  // Auto-vul omschrijving wanneer type/offerte wijzigen (tenzij user manueel aangepast).
  // Percentage zit BEWUST niet in de omschrijving — die hoort in de bedrag-flow.
  const [descTouched, setDescTouched] = useState(false);
  useEffect(() => {
    if (!info || descTouched) return;
    const qDate = info.quote_date_snapshot || info.quote_date;
    setDescription(_defaultDescription(type, info.quote_number, qDate));
  }, [type, info, descTouched]);

  const canSubmit = info?.invoiceable && computedAmount > 0 && computedAmount <= remaining + 0.005;

  const onSubmit = async () => {
    if (!canSubmit || !info) return;
    setSubmitting(true);
    try {
      const inv = await createInvoiceFromQuote(quoteId, {
        invoice_type: type,
        tax_regime_key: regime,
        line_items: [{
          description: description || _defaultDescription(type, info.quote_number, info.quote_date_snapshot || info.quote_date),
          quantity: 1,
          unit: "fixed",
          unit_price_excl: computedAmount,
          vat_rate: effectiveVatRate,
          line_type: "service",
          sort_order: 0,
        }],
        notes: "",
      });
      toast.success("Concept-factuur aangemaakt.");
      onOpenChange(false);
      navigate(`/facturen/${inv.id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Aanmaken mislukt.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setQuoteId(""); setInfo(null); setType("deposit");
    setMode("percent"); setPercent(30); setAmount(0);
    setDescription(""); setDescTouched(false);
    setRegime("standard");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent
        className="max-w-2xl w-[calc(100vw-1rem)] sm:w-full p-0 max-h-[95dvh] sm:max-h-[90vh] overflow-hidden flex flex-col gap-0"
        data-testid="factuur-wizard"
      >
        {/* Sticky header — mobiele "← Facturen" + titel + Radix X */}
        <div className="flex-shrink-0 border-b border-[#E5E2DC] bg-white px-4 sm:px-6 py-3 sm:py-4">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="md:hidden inline-flex items-center gap-1 text-sm text-[#5D7259] font-semibold hover:underline mb-2 -ml-1"
            data-testid="factuur-wizard-back-mobile"
          >
            <ArrowLeft className="w-4 h-4" /> Facturen
          </button>
          <DialogTitle className="font-heading text-xl sm:text-2xl text-[#2C2C2A] pr-8">Factuur vanuit offerte</DialogTitle>
          <DialogDescription className="text-xs sm:text-sm text-[#4F4F4D] mt-1">
            Kies een goedgekeurde offerte en het type factuur — de rest vullen we automatisch in vanuit de offerte.
          </DialogDescription>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-5">
          {/* Stap 1 — offerte-picker */}
          <div className="space-y-2">
            <Label htmlFor="quote-picker" className="text-sm font-medium text-[#2C2C2A]">1. Kies offerte</Label>
            <Select value={quoteId} onValueChange={setQuoteId} disabled={!!presetQuoteId}>
              <SelectTrigger id="quote-picker" className="h-11 rounded-xl" data-testid="factuur-wizard-quote-picker">
                <SelectValue placeholder="Selecteer een goedgekeurde offerte" />
              </SelectTrigger>
              <SelectContent>
                {quotes.length === 0 && (
                  <div className="px-2 py-3 text-sm text-[#4F4F4D] italic">
                    Geen goedgekeurde offertes gevonden.
                  </div>
                )}
                {quotes.map((q) => (
                  <SelectItem key={q.id} value={q.id}>
                    {(q.quote_number || "—")} · {q.client_name || "?"} · {q.project_name || "?"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {infoLoading && (
            <div className="flex items-center gap-2 text-sm text-[#4F4F4D]">
              <Loader2 className="w-4 h-4 animate-spin" /> Offertegegevens laden…
            </div>
          )}

          {info && !info.invoiceable && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-[#FBE9DF] border border-[#F0C4A3] text-sm text-[#7F4124]" data-testid="factuur-wizard-blocker">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{info.blocker_reason}</span>
            </div>
          )}

          {info && info.invoiceable && (
            <>
              {/* Overzichtskaart offerte */}
              <div className="rounded-xl bg-[#FAF9F6] border border-[#E5E2DC] p-4 space-y-1" data-testid="factuur-wizard-info">
                <div className="flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-[#5D7259] font-semibold">
                  <FileText className="w-3.5 h-3.5" /> Offerte {info.quote_number}
                </div>
                <div className="grid grid-cols-2 gap-1 text-sm mt-1">
                  <div className="text-[#4F4F4D]">Offertebedrag excl.</div>
                  <div className="text-right font-medium text-[#2C2C2A]">{fmtEur(info.quote_total_excl)}</div>
                  <div className="text-[#4F4F4D]">Reeds gefactureerd</div>
                  <div className="text-right text-[#4F4F4D]">{fmtEur(info.previously_invoiced_excl)}</div>
                  <div className="text-[#2C2C2A] font-medium">Nog te factureren excl.</div>
                  <div className="text-right font-bold text-[#D96C4A]" data-testid="factuur-wizard-remaining">{fmtEur(remaining)}</div>
                </div>
              </div>

              {/* Stap 2 — type */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2A]">2. Type factuur</Label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(TYPE_LABELS).map(([v, label]) => (
                    <button
                      key={v} type="button"
                      onClick={() => { setType(v); setDescTouched(false); }}
                      className={`h-11 rounded-xl border text-sm font-medium transition ${
                        type === v
                          ? "bg-[#B07A4F] text-white border-[#B07A4F]"
                          : "bg-white text-[#2C2C2A] border-[#E5E2DC] hover:bg-[#F0EFEA]"
                      }`}
                      data-testid={`factuur-wizard-type-${v}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stap 3 — percentage of vast bedrag */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-[#2C2C2A]">3. Bedrag</Label>
                  <div className="flex gap-1 text-xs">
                    <button type="button" onClick={() => setMode("percent")}
                      className={`px-2.5 py-1 rounded-full border ${mode === "percent" ? "bg-[#B07A4F] text-white border-[#B07A4F]" : "bg-white text-[#2C2C2A] border-[#E5E2DC]"}`}
                      data-testid="factuur-wizard-mode-percent">Percentage</button>
                    <button type="button" onClick={() => setMode("amount")}
                      className={`px-2.5 py-1 rounded-full border ${mode === "amount" ? "bg-[#B07A4F] text-white border-[#B07A4F]" : "bg-white text-[#2C2C2A] border-[#E5E2DC]"}`}
                      data-testid="factuur-wizard-mode-amount">Vast bedrag</button>
                  </div>
                </div>
                {mode === "percent" ? (
                  <>
                    <div className="flex gap-2 flex-wrap">
                      {[10, 25, 30, 40, 50, 100].map((p) => (
                        <button key={p} type="button" onClick={() => setPercent(p)}
                          className={`h-9 px-3 rounded-full border text-sm ${
                            percent === p ? "bg-[#B07A4F] text-white border-[#B07A4F]" : "bg-white text-[#2C2C2A] border-[#E5E2DC] hover:bg-[#F0EFEA]"
                          }`}
                          data-testid={`factuur-wizard-percent-${p}`}
                        >{p}%</button>
                      ))}
                    </div>
                    <Input type="number" min="0" max="100" step="0.1" value={percent}
                      onChange={(e) => setPercent(parseFloat(e.target.value) || 0)}
                      className="h-11 rounded-xl" data-testid="factuur-wizard-percent-input" />
                  </>
                ) : (
                  <Input type="number" min="0" step="0.01" value={amount}
                    onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                    placeholder="Bedrag excl. BTW"
                    className="h-11 rounded-xl" data-testid="factuur-wizard-amount-input" />
                )}
              </div>

              {/* Stap 4 — omschrijving (auto ingevuld, editable) */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#2C2C2A]">4. Omschrijving <span className="text-xs text-[#A8A7A0]">(optioneel aan te passen)</span></Label>
                <Input
                  value={description}
                  onChange={(e) => { setDescription(e.target.value); setDescTouched(true); }}
                  className="h-11 rounded-xl"
                  data-testid="factuur-wizard-description"
                />
              </div>

              {/* v6 F3.5b — Stap 5: BTW-regime (klant-erfelijk, wijzigbaar per factuur) */}
              <div className="space-y-2">
                <Label htmlFor="fw-regime" className="text-sm font-medium text-[#2C2C2A]">5. BTW-regime</Label>
                <select
                  id="fw-regime"
                  value={regime}
                  onChange={(e) => setRegime(e.target.value)}
                  className="mt-1 flex h-11 w-full rounded-xl border border-[#E5E2DC] bg-white px-3 text-sm"
                  data-testid="factuur-wizard-regime"
                >
                  {TAX_REGIME_KEYS.map((k) => (
                    <option key={k} value={k}>{TAX_REGIME_LABELS[k]}</option>
                  ))}
                </select>
                <p className="text-[11px] text-[#6E6E6C] leading-snug">
                  {TAX_REGIME_HINTS[regime]}
                </p>
                {/* v14q — Uitleg: regime ≠ tarief */}
                <p className="text-[11px] text-[#6E6E6C] leading-snug italic" data-testid="factuur-wizard-regime-hint">
                  BTW-regime bepaalt de fiscale vermelding op de factuur. Het BTW-percentage wordt overgenomen uit de offerte.
                </p>
                {/* v14q — Coherentie-waarschuwing: regime 6% ↔ offerte 21% */}
                {showRegimeMismatchWarning && (
                  <div
                    className="flex items-start gap-2 p-3 rounded-xl bg-[#FFF4E5] border border-[#F0C97E] text-xs text-[#6B4A08]"
                    data-testid="factuur-wizard-regime-mismatch"
                  >
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 text-[#B87700] flex-shrink-0" />
                    <span>
                      De offerte gebruikt 21% BTW, maar je hebt Renovatie 6% geselecteerd. Pas eerst de BTW van de offerte aan als je 6% wilt factureren.
                    </span>
                  </div>
                )}
              </div>

              {isFinal && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-[#F0EFEA] border border-[#E5E2DC] text-xs text-[#4F4F4D]" data-testid="factuur-wizard-final-notice">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 text-[#5D7259]" />
                  <span>
                    Eindfactuur factureert altijd exact het resterend saldo
                    ({fmtEur(remaining)}), ongeacht een ingesteld percentage.
                  </span>
                </div>
              )}

              {/* Live preview */}
              <div className="rounded-xl border border-[#E5E2DC] p-4 bg-white" data-testid="factuur-wizard-preview">
                <div className="text-[11px] tracking-[0.18em] uppercase text-[#5D7259] font-semibold mb-2">Voorbeeld</div>
                <div className="grid grid-cols-2 gap-1 text-sm">
                  <div className="text-[#4F4F4D]">Subtotaal excl.</div>
                  <div className="text-right text-[#2C2C2A]">{fmtEur(computedAmount)}</div>
                  <div className="text-[#4F4F4D]">
                    {isReverseCharge
                      ? "BTW (verlegd)"
                      : effectiveVatRate !== vatRate
                        ? `BTW volgens regime ${effectiveVatRate}%`
                        : `BTW uit offerte ${vatBucketsLabel}`}
                  </div>
                  <div className="text-right text-[#2C2C2A]">
                    {isReverseCharge ? <span className="italic text-[#5D7259]">— verlegd —</span> : fmtEur(previewVat)}
                  </div>
                  <div className="text-[#2C2C2A] font-medium pt-1 border-t border-[#F0EFEA]">Totaal incl.</div>
                  <div className="text-right font-bold text-[#2C2C2A] pt-1 border-t border-[#F0EFEA]" data-testid="factuur-wizard-preview-incl">{fmtEur(previewIncl)}</div>
                </div>
                {computedAmount > remaining + 0.005 && (
                  <div className="mt-2 flex items-start gap-1.5 text-xs text-[#C0392B]">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5" />
                    <span>Bedrag overschrijdt het resterend factureerbaar saldo.</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Sticky footer */}
        <div className="flex-shrink-0 border-t border-[#E5E2DC] bg-white px-4 sm:px-6 py-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="h-11 rounded-xl"
            data-testid="factuur-wizard-cancel"
          >
            Annuleer
          </Button>
          <Button
            onClick={onSubmit} disabled={!canSubmit || submitting}
            className="h-11 rounded-xl bg-[#2C2C2A] hover:bg-[#1E1E1D] text-white"
            data-testid="factuur-wizard-submit"
          >
            {submitting ? "Bezig…" : "Concept aanmaken"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
